<template>
  <div class="pdf-toolbar">
    <!-- Left: Thumbnails toggle + Page indicator -->
    <div class="pdf-toolbar__section pdf-toolbar__section--left">
      <button
        type="button"
        class="pdf-toolbar__icon-btn"
        :class="{ 'pdf-toolbar__icon-btn--active': showThumbnails }"
        @click="emit('update:showThumbnails', !showThumbnails)"
      >
        <v-icon :icon="mdiPageLayoutSidebarLeft" size="18" />
        <v-tooltip activator="parent" location="bottom">{{ t('toolbar.thumbnails') }}</v-tooltip>
      </button>

      <!-- Page indicator -->
      <div v-if="totalPages > 0" class="pdf-toolbar__page-indicator">
        <button
          type="button"
          class="pdf-toolbar__icon-btn"
          :disabled="currentPage <= 1"
          @click="emit('prevPage')"
        >
          <v-icon :icon="mdiChevronUp" size="18" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.prevPage') }}</v-tooltip>
        </button>

        <div class="pdf-toolbar__page-input-group">
          <input
            type="text"
            inputmode="numeric"
            class="pdf-toolbar__page-input"
            :value="currentPage"
            :aria-label="t('toolbar.pageNumber')"
            @keydown.enter="handlePageInput($event)"
            @blur="handlePageInput($event)"
          />
          <span class="pdf-toolbar__page-separator">/</span>
          <span class="pdf-toolbar__page-total">{{ totalPages }}</span>
        </div>

        <button
          type="button"
          class="pdf-toolbar__icon-btn"
          :disabled="currentPage >= totalPages"
          @click="emit('nextPage')"
        >
          <v-icon :icon="mdiChevronDown" size="18" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.nextPage') }}</v-tooltip>
        </button>
      </div>
    </div>

    <!-- Center: Zoom controls -->
    <div class="pdf-toolbar__section pdf-toolbar__section--center">
      <div class="pdf-toolbar__group">
        <button
          type="button"
          class="pdf-toolbar__icon-btn"
          @click="emit('zoomOut')"
        >
          <v-icon :icon="mdiMinus" size="16" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.zoomOut') }}</v-tooltip>
        </button>

        <v-menu>
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              type="button"
              class="pdf-toolbar__zoom-btn"
            >
              {{ formatZoom(zoom) }}
              <v-icon :icon="mdiChevronDown" size="14" />
            </button>
          </template>

          <v-card class="pdf-toolbar__zoom-menu" elevation="3">
            <v-list density="compact" nav>
              <v-list-item
                :active="zoomMode === 'auto'"
                @click="emit('setZoom', 'auto')"
              >
                <v-list-item-title>{{ t('toolbar.zoomAuto') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :active="zoomMode === 'fit-width'"
                @click="emit('setZoom', 'fit-width')"
              >
                <v-list-item-title>{{ t('toolbar.zoomFitWidth') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :active="zoomMode === 'fit-page'"
                @click="emit('setZoom', 'fit-page')"
              >
                <v-list-item-title>{{ t('toolbar.zoomFitPage') }}</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" />

              <v-list-item
                v-for="preset in zoomPresets"
                :key="preset"
                :active="zoom === preset && !zoomMode"
                @click="emit('setZoom', preset)"
              >
                <v-list-item-title>{{ preset }}%</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <button
          type="button"
          class="pdf-toolbar__icon-btn"
          @click="emit('zoomIn')"
        >
          <v-icon :icon="mdiPlus" size="16" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.zoomIn') }}</v-tooltip>
        </button>
      </div>
    </div>

    <!-- Right: View mode + Actions -->
    <div class="pdf-toolbar__section pdf-toolbar__section--right">
      <!-- Desktop -->
      <template v-if="!mobile">
        <button
          v-if="enableSearch"
          type="button"
          class="pdf-toolbar__icon-btn"
          :class="{ 'pdf-toolbar__icon-btn--active': showSearch }"
          @click="emit('update:showSearch', !showSearch)"
        >
          <v-icon :icon="mdiMagnify" size="18" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.search') }}</v-tooltip>
        </button>

        <!-- View mode segmented control -->
        <div class="pdf-toolbar__segmented">
          <button
            type="button"
            class="pdf-toolbar__segment"
            :class="{ 'pdf-toolbar__segment--active': viewMode === 'continuous' }"
            @click="emit('setViewMode', 'continuous')"
          >
            <v-icon :icon="mdiViewSequential" size="16" />
            <v-tooltip activator="parent" location="bottom">{{ t('toolbar.singlePage') }}</v-tooltip>
          </button>
          <button
            type="button"
            class="pdf-toolbar__segment"
            :class="{ 'pdf-toolbar__segment--active': viewMode === 'two-page' }"
            @click="emit('setViewMode', 'two-page')"
          >
            <v-icon :icon="mdiBookOpenPageVariant" size="16" />
            <v-tooltip activator="parent" location="bottom">{{ t('toolbar.twoPages') }}</v-tooltip>
          </button>
        </div>

        <button
          v-if="enableRotation"
          type="button"
          class="pdf-toolbar__icon-btn"
          @click="emit('rotate')"
        >
          <v-icon :icon="mdiRotateRight" size="18" />
          <v-tooltip activator="parent" location="bottom">{{ t('toolbar.rotate') }}</v-tooltip>
        </button>

        <div class="pdf-toolbar__group">
          <button
            v-if="enablePrint"
            type="button"
            class="pdf-toolbar__icon-btn"
            @click="emit('print')"
          >
            <v-icon :icon="mdiPrinter" size="18" />
            <v-tooltip activator="parent" location="bottom">{{ t('toolbar.print') }}</v-tooltip>
          </button>

          <button
            v-if="enableDownload"
            type="button"
            class="pdf-toolbar__icon-btn"
            @click="emit('download')"
          >
            <v-icon :icon="mdiDownload" size="18" />
            <v-tooltip activator="parent" location="bottom">{{ t('toolbar.download') }}</v-tooltip>
          </button>

          <button
            type="button"
            class="pdf-toolbar__icon-btn"
            @click="emit('toggleFullscreen')"
          >
            <v-icon :icon="isFullscreen ? mdiFullscreenExit : mdiFullscreen" size="18" />
            <v-tooltip activator="parent" location="bottom">
              {{ isFullscreen ? t('toolbar.exitFullscreen') : t('toolbar.fullscreen') }}
            </v-tooltip>
          </button>
        </div>
      </template>

      <!-- Mobile: overflow menu -->
      <template v-else>
        <v-menu>
          <template #activator="{ props: menuProps }">
            <button v-bind="menuProps" type="button" class="pdf-toolbar__icon-btn">
              <v-icon :icon="mdiDotsVertical" size="18" />
            </button>
          </template>
          <v-card class="pdf-toolbar__mobile-menu" elevation="3">
            <v-list density="compact" nav>
              <v-list-item
                v-if="enableSearch"
                @click="emit('update:showSearch', !showSearch)"
              >
                <template #prepend>
                  <v-icon size="18" :icon="mdiMagnify" />
                </template>
                <v-list-item-title>{{ t('toolbar.search') }}</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" />

              <v-list-item @click="emit('setViewMode', 'continuous')">
                <template #prepend>
                  <v-icon size="18" :icon="mdiViewSequential" :color="viewMode === 'continuous' ? 'primary' : undefined" />
                </template>
                <v-list-item-title>{{ t('toolbar.singlePage') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="emit('setViewMode', 'two-page')">
                <template #prepend>
                  <v-icon size="18" :icon="mdiBookOpenPageVariant" :color="viewMode === 'two-page' ? 'primary' : undefined" />
                </template>
                <v-list-item-title>{{ t('toolbar.twoPages') }}</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" />

              <v-list-item v-if="enableRotation" @click="emit('rotate')">
                <template #prepend>
                  <v-icon size="18" :icon="mdiRotateRight" />
                </template>
                <v-list-item-title>{{ t('toolbar.rotate') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="enablePrint" @click="emit('print')">
                <template #prepend>
                  <v-icon size="18" :icon="mdiPrinter" />
                </template>
                <v-list-item-title>{{ t('toolbar.print') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="enableDownload" @click="emit('download')">
                <template #prepend>
                  <v-icon size="18" :icon="mdiDownload" />
                </template>
                <v-list-item-title>{{ t('toolbar.download') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="emit('toggleFullscreen')">
                <template #prepend>
                  <v-icon size="18" :icon="isFullscreen ? mdiFullscreenExit : mdiFullscreen" />
                </template>
                <v-list-item-title>
                  {{ isFullscreen ? t('toolbar.exitFullscreen') : t('toolbar.fullscreen') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiPlus,
  mdiMinus,
  mdiMagnify,
  mdiPrinter,
  mdiDownload,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiPageLayoutSidebarLeft,
  mdiViewSequential,
  mdiBookOpenPageVariant,
  mdiRotateRight,
  mdiDotsVertical,
} from '@mdi/js'
import { useDisplay } from 'vuetify'
import type { PDFViewMode, PDFZoomMode, PDFZoom } from '../types'
import { formatZoom } from '../utils/zoom-utils'
import { usePDFI18n } from '../i18n'

const { mobile } = useDisplay()
const { t } = usePDFI18n()

const props = defineProps<{
  currentPage: number
  totalPages: number
  zoom: number
  zoomMode: PDFZoomMode | null
  zoomPresets: number[]
  viewMode: PDFViewMode
  isFullscreen: boolean
  showThumbnails: boolean
  showSearch: boolean
  enableSearch?: boolean
  enablePrint?: boolean
  enableDownload?: boolean
  enableRotation?: boolean
}>()

const emit = defineEmits<{
  prevPage: []
  nextPage: []
  goToPage: [page: number]
  zoomIn: []
  zoomOut: []
  setZoom: [zoom: PDFZoom]
  setViewMode: [mode: PDFViewMode]
  toggleFullscreen: []
  rotate: []
  print: []
  download: []
  'update:showThumbnails': [show: boolean]
  'update:showSearch': [show: boolean]
}>()

function handlePageInput(event: Event): void {
  const input = event.target as HTMLInputElement
  const value = parseInt(input.value, 10)
  if (!isNaN(value) && value >= 1 && value <= props.totalPages) {
    emit('goToPage', value)
  } else {
    input.value = String(props.currentPage)
  }
}
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-xs $space-md;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), $opacity-border);
  min-height: 44px;
  gap: $space-sm;

  // ── Sections ──
  &__section {
    display: flex;
    align-items: center;
    gap: $space-sm;

    &--left {
      flex: 1;
      justify-content: flex-start;
    }

    &--center {
      flex: 0 0 auto;
    }

    &--right {
      flex: 1;
      justify-content: flex-end;
    }
  }

  // ── Page indicator ──
  &__page-indicator {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__page-input-group {
    display: flex;
    align-items: center;
    gap: $space-xs;
    padding: 0 $space-xs;
  }

  &__page-input {
    width: 36px;
    height: 28px;
    border: 1px solid rgba(var(--v-border-color), $opacity-border);
    border-radius: $radius-sm;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    font-size: $text-sm;
    font-weight: $font-medium;
    text-align: center;
    outline: none;
    @include transition-fast;

    &:focus {
      border-color: rgb(var(--v-theme-primary));
      box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
    }
  }

  &__page-separator {
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    font-size: $text-sm;
    user-select: none;
  }

  &__page-total {
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    font-size: $text-sm;
    font-weight: $font-medium;
    user-select: none;
  }

  // ── Icon button (base) ──
  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: $radius-md;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    cursor: pointer;
    flex-shrink: 0;
    @include transition-fast;
    @include focus-visible-only;

    &:hover {
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
      background: rgba(var(--v-theme-on-surface), 0.08);
    }

    &:active {
      transform: scale(0.92);
    }

    &--active {
      color: rgb(var(--v-theme-primary));
      background: rgba(var(--v-theme-primary), 0.1);

      &:hover {
        color: rgb(var(--v-theme-primary));
        background: rgba(var(--v-theme-primary), 0.15);
      }
    }

    &:disabled {
      opacity: $opacity-disabled;
      cursor: default;
      pointer-events: none;
    }
  }

  // ── Control group (pill background) ──
  &__group {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 2px;
    background: rgba(var(--v-theme-on-surface), 0.04);
    border-radius: $radius-md;
  }

  // ── Zoom button ──
  &__zoom-btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: $space-xs $space-sm;
    border: none;
    border-radius: $radius-sm;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    font-size: $text-sm;
    font-weight: $font-medium;
    letter-spacing: $tracking-wide;
    cursor: pointer;
    white-space: nowrap;
    min-width: 56px;
    justify-content: center;
    @include transition-fast;
    @include focus-visible-only;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
    }
  }

  // ── Zoom dropdown menu ──
  &__zoom-menu {
    border-radius: 10px !important;
    min-width: 160px;

    :deep(.v-list) {
      padding: $space-xs;
    }

    :deep(.v-list-item) {
      min-height: 34px;
      padding: 0 $space-md;
      border-radius: 6px;
      margin: 1px 0;
    }

    :deep(.v-list-item-title) {
      font-size: $text-base;
      font-weight: $font-normal;
    }
  }

  // ── View mode segmented control ──
  &__segmented {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 2px;
    background: rgba(var(--v-theme-on-surface), 0.06);
    border-radius: $radius-md;
  }

  &__segment {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    cursor: pointer;
    @include transition-fast;
    @include focus-visible-only;

    &:hover {
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
    }

    &--active {
      background: rgb(var(--v-theme-surface));
      color: rgb(var(--v-theme-primary));
      box-shadow: $shadow-xs;
    }
  }

  // ── Mobile menu ──
  &__mobile-menu {
    border-radius: 10px !important;
    min-width: 180px;

    :deep(.v-list) {
      padding: $space-xs;
    }

    :deep(.v-list-item) {
      min-height: 36px;
      padding: 0 $space-md;
      border-radius: 6px;
      margin: 1px 0;
    }

    :deep(.v-list-item-title) {
      font-size: $text-base;
      font-weight: $font-normal;
    }
  }

  // ── Reduced motion ──
  @include reduced-motion {
    .pdf-toolbar__icon-btn,
    .pdf-toolbar__zoom-btn,
    .pdf-toolbar__segment {
      transition: none;

      &:active {
        transform: none;
      }
    }
  }

  // ── Mobile adjustments ──
  @media (max-width: 600px) {
    padding: $space-xs $space-sm;
    gap: $space-xs;
  }
}
</style>
